import { z } from "zod";

import { OpaqueIdSchema, UsernameSchema } from "./identity-schemas";
import { DirectoryEmailSchema } from "./workspace-user-schemas";

/**
 * Customer-approved support access (ADR-0072).
 *
 * One module for both sides of the decision on purpose. The employee asking in the operations
 * console and the administrator answering in the workspace portal are looking at one row, and the
 * only way to guarantee an approval notice cannot describe a different window, scope, or account
 * from the one being enforced is for both surfaces to parse the same projection.
 */

export const SupportAccessScopeSchema = z.enum(["tool", "organization"]);
export type SupportAccessScope = z.infer<typeof SupportAccessScopeSchema>;

export const SupportAccessModeSchema = z.enum(["read", "write"]);
export type SupportAccessMode = z.infer<typeof SupportAccessModeSchema>;

/**
 * `expired` and `revoked` are separate terminal states rather than one "ended". They mean different
 * things to a customer reading the history — the clock ran out, versus somebody here stopped it —
 * and they are notified with different words.
 */
export const SupportAccessStatusSchema = z.enum([
  "pending",
  "approved",
  "denied",
  "expired",
  "revoked",
]);
export type SupportAccessStatus = z.infer<typeof SupportAccessStatusSchema>;

/**
 * A fixed menu, not a free number of seconds. An administrator approves or denies exactly what was
 * asked for, so the set of things they can be asked for stays small enough to recognise at a
 * glance, and 24 hours is the ceiling no request can exceed.
 */
export const SupportAccessDurationSchema = z.enum(["1h", "4h", "8h", "24h"]);
export type SupportAccessDuration = z.infer<typeof SupportAccessDurationSchema>;

export const SUPPORT_ACCESS_DURATION_SECONDS: Record<SupportAccessDuration, number> = {
  "1h": 60 * 60,
  "4h": 4 * 60 * 60,
  "8h": 8 * 60 * 60,
  "24h": 24 * 60 * 60,
};

export const SUPPORT_ACCESS_DURATION_LABEL: Record<SupportAccessDuration, string> = {
  "1h": "1 hour",
  "4h": "4 hours",
  "8h": "8 hours",
  "24h": "24 hours",
};

/**
 * A stored duration rendered back for a person. Reads off the menu when it matches one, which it
 * always does for anything this platform created; the fallback keeps a hand-written row legible
 * rather than printing raw seconds at somebody deciding whether to consent to it.
 */
export function formatSupportAccessDuration(seconds: number): string {
  const match = SUPPORT_ACCESS_DURATION_SECONDS_TO_LABEL[seconds];
  if (match) return match;
  // Whole hours only when it divides evenly, minutes otherwise. Nothing here rounds a window up:
  // this label is what somebody consents to, and telling them "1 hour" for ninety minutes — or for
  // thirty — is the one error that matters in either direction.
  if (seconds % 3600 === 0) {
    const hours = seconds / 3600;
    return hours === 1 ? "1 hour" : `${hours} hours`;
  }
  const minutes = Math.max(1, Math.round(seconds / 60));
  return minutes === 1 ? "1 minute" : `${minutes} minutes`;
}

const SUPPORT_ACCESS_DURATION_SECONDS_TO_LABEL: Record<number, string> = Object.fromEntries(
  SupportAccessDurationSchema.options.map((duration) => [
    SUPPORT_ACCESS_DURATION_SECONDS[duration],
    SUPPORT_ACCESS_DURATION_LABEL[duration],
  ]),
);

/**
 * What an employee submits. `confirmWrite` mirrors `OpsSupportSessionCreateSchema`: write access is
 * never the consequence of a default, and the server refuses the request rather than quietly
 * downgrading it to read.
 */
export const SupportAccessRequestCreateSchema = z
  .strictObject({
    targetUserId: OpaqueIdSchema,
    scope: SupportAccessScopeSchema,
    toolId: OpaqueIdSchema.optional(),
    mode: SupportAccessModeSchema.default("read"),
    duration: SupportAccessDurationSchema,
    reason: z.string().trim().min(10).max(500),
    confirmWrite: z.boolean().default(false),
  })
  .superRefine((value, context) => {
    if (value.scope === "tool" && value.toolId === undefined) {
      context.addIssue({
        code: "custom",
        path: ["toolId"],
        message: "Tool-scoped access must name the tool.",
      });
    }
    if (value.scope === "organization" && value.toolId !== undefined) {
      context.addIssue({
        code: "custom",
        path: ["toolId"],
        message: "Organization-wide access does not name a tool.",
      });
    }
    if (value.mode === "write" && !value.confirmWrite) {
      context.addIssue({
        code: "custom",
        path: ["confirmWrite"],
        message: "Write support access requires explicit confirmation.",
      });
    }
  });
export type SupportAccessRequestCreate = z.infer<typeof SupportAccessRequestCreateSchema>;

/** What an administrator submits. Approve or deny; the requested window is not editable here. */
export const SupportAccessDecisionSchema = z.strictObject({
  decision: z.enum(["approve", "deny"]),
});
export type SupportAccessDecision = z.infer<typeof SupportAccessDecisionSchema>;

const SupportAccessPersonSchema = z.strictObject({
  displayName: z.string().min(1).max(120),
});

/**
 * The requesting employee, named. Support access is a delegation rather than impersonation that
 * hides who is behind it, so the administrator deciding always sees a person and an address they
 * could reply to, never a role or a generic support identity.
 */
const SupportAccessRequesterSchema = z.strictObject({
  displayName: z.string().min(1).max(120),
  email: DirectoryEmailSchema.nullable(),
});

/** The customer account the employee would act as, shown to the administrator before deciding. */
const SupportAccessTargetSchema = z.strictObject({
  id: OpaqueIdSchema,
  displayName: z.string().min(1).max(120),
  username: UsernameSchema,
});

const SupportAccessToolSchema = z.strictObject({
  id: OpaqueIdSchema,
  displayName: z.string().min(1).max(120),
});

export const SupportAccessRequestSchema = z.strictObject({
  id: OpaqueIdSchema,
  organizationId: OpaqueIdSchema,
  /**
   * The workspace's own name, read back with the request rather than looked up beside it.
   *
   * One row, one name: the portal heading, the operations console, the audit payload, and the
   * notification all describe the same workspace because they all read the same field.
   */
  organizationName: z.string().min(1).max(200),
  scope: SupportAccessScopeSchema,
  /** Present exactly when the scope is `tool`, which the database CHECK also guarantees. */
  tool: SupportAccessToolSchema.nullable(),
  mode: SupportAccessModeSchema,
  reason: z.string().min(10).max(500),
  status: SupportAccessStatusSchema,
  requester: SupportAccessRequesterSchema,
  targetUser: SupportAccessTargetSchema,
  createdAt: z.number().int().positive(),
  /**
   * How long was asked for, as a length. The window only becomes a pair of instants when somebody
   * approves it, because approval is when the clock starts.
   */
  requestedDurationSeconds: z.number().int().positive().max(86_400),
  decidedAt: z.number().int().positive().nullable(),
  decidedBy: SupportAccessPersonSchema.nullable(),
  expiresAt: z.number().int().positive().nullable(),
  revokedAt: z.number().int().positive().nullable(),
  /**
   * Whether this approval is usable right now, which is not the same question as its status: a row
   * reads `approved` for the interval between its expiry passing and the scheduled sweep noticing.
   *
   * Computed on the server against the same clock every authorization join uses, rather than in the
   * browser against the viewer's. A screen deciding this locally would offer "End access now" on
   * something already over, or hide it from something still live, on a machine whose clock is
   * merely wrong.
   */
  live: z.boolean(),
});
export type SupportAccessRequest = z.infer<typeof SupportAccessRequestSchema>;

export const SupportAccessRequestsResponseSchema = z.strictObject({
  requests: z.array(SupportAccessRequestSchema).max(50),
});
export type SupportAccessRequestsResponse = z.infer<typeof SupportAccessRequestsResponseSchema>;

export const SupportAccessRequestResponseSchema = z.strictObject({
  request: SupportAccessRequestSchema,
});
export type SupportAccessRequestResponse = z.infer<typeof SupportAccessRequestResponseSchema>;
